import React from "react";
import { Form, Row, Col, Card } from "react-bootstrap";
import * as uuid from "uuid";
import isEmpty from "lodash/isEmpty";
import {
  ADDRESS_TYPES,
  embeddedSpacesPattern,
  alphaNumericWithSpecialCharacterPattern,
  pincodePattern
} from "../../constants/constants";
import { addressObject } from "../../constants/candidateDataObjects";
import useValidation from "../../util/useValidation";

export const AddressDetailsForm = ({
  candidateId,
  addressType,
  addressDetails,
  states,
  updateCandidateAddressDetails,
  addCandidateAddressDetails
}) => {
  const { handleOnBlurValidation, errors } = useValidation();

  const getAddressHeaderTitle = addressType => {
    switch (addressType) {
      case ADDRESS_TYPES.PERMANENT:
        return "Permanent Address";
      case ADDRESS_TYPES.CURRENT:
        return "Current Address";
      default:
        return "Address";
    }
  };

  const handleOnChangeAddressDetails = e => {
    const { name, value } = e.target;
    if (embeddedSpacesPattern.test(value)) {
      if (!isEmpty(addressDetails) && addressDetails.id) {
        updateCandidateAddressDetails(
          addressDetails.id,
          name,
          value.trimStart()
        );
      } else {
        const defaultAddressObj = addressObject();
        const newAddress = {
          ...defaultAddressObj,
          id: `temp_${uuid.v4()}`,
          candidateId,
          addressType: addressType,
          [name]: value.trimStart()
        };
        addCandidateAddressDetails(newAddress);
      }
    }
  };
  const handleOnBlurInput = e => {
    handleOnBlurValidation(e);
  };

  return (
    <Card>
      <Card.Header className="accordian-header">
        <Row>
          <Col sm={11}>{getAddressHeaderTitle(addressType)}</Col>
        </Row>
      </Card.Header>
      <Card.Body>
        <Form.Group as={Row} controlId="line1">
          <Form.Label column sm={2} className="text-right">
            Address Line1*
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              size="sm"
              required
              placeholder="Wing Name & Flat or House Number"
              type="text"
              name="addressLine1"
              value={addressDetails.addressLine1}
              onChange={handleOnChangeAddressDetails}
              onBlur={handleOnBlurInput}
              isInvalid={errors.addressLine1}
              pattern={alphaNumericWithSpecialCharacterPattern}
            />

            {errors.addressLine1 && (
              <Form.Control.Feedback type="invalid">
                {errors.addressLine1.type === "required" &&
                  "Address Line1 is required"}
                {errors.addressLine1.type === "pattern" &&
                  "Invalid characters entered for Address Line1"}
              </Form.Control.Feedback>
            )}
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="line2">
          <Form.Label column sm={2} className="text-right">
            Address Line2*
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              size="sm"
              required
              placeholder="Apartment or Building Name"
              type="text"
              name="addressLine2"
              value={addressDetails.addressLine2}
              onChange={handleOnChangeAddressDetails}
              onBlur={handleOnBlurInput}
              isInvalid={errors.addressLine2}
              pattern={alphaNumericWithSpecialCharacterPattern}
            />

            {errors.addressLine2 && (
              <Form.Control.Feedback type="invalid">
                {errors.addressLine2.type === "required" &&
                  "Address Line2 is required"}
                {errors.addressLine2.type === "pattern" &&
                  "Invalid characters entered for Address Line2"}
              </Form.Control.Feedback>
            )}
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="localityLandMark">
          <Form.Label column sm={2} className="text-right">
            Locality*
          </Form.Label>
          <Col sm={3}>
            <Form.Control
              size="sm"
              required
              placeholder="Locality"
              type="text"
              name="locality"
              value={addressDetails.locality}
              onChange={handleOnChangeAddressDetails}
              onBlur={handleOnBlurInput}
              isInvalid={errors.locality}
              pattern={alphaNumericWithSpecialCharacterPattern}
            />

            {errors.locality && (
              <Form.Control.Feedback type="invalid">
                {errors.locality.type === "required" && "Locality is required"}
                {errors.locality.type === "pattern" &&
                  "Invalid characters entered for Locality"}
              </Form.Control.Feedback>
            )}
          </Col>

          <Form.Label column sm={3} className="text-right">
            Landmark*
          </Form.Label>
          <Col sm={3}>
            <Form.Control
              size="sm"
              required
              placeholder="Landmark"
              type="text"
              name="landmark"
              value={addressDetails.landmark}
              onChange={handleOnChangeAddressDetails}
              onBlur={handleOnBlurInput}
              isInvalid={errors.landmark}
              pattern={alphaNumericWithSpecialCharacterPattern}
            />

            {errors.landmark && (
              <Form.Control.Feedback type="invalid">
                {errors.landmark.type === "required" && "Landmark is required"}
                {errors.landmark.type === "pattern" &&
                  "Invalid characters entered for Landmark"}
              </Form.Control.Feedback>
            )}
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="location">
          <Form.Label column sm={2} className="text-right">
            City*
          </Form.Label>
          <Col sm={3}>
            <Form.Control
              size="sm"
              required
              placeholder="City"
              type="text"
              name="city"
              value={addressDetails.city}
              onChange={handleOnChangeAddressDetails}
              onBlur={handleOnBlurInput}
              isInvalid={errors.city}
              pattern={alphaNumericWithSpecialCharacterPattern}
            />

            {errors.city && (
              <Form.Control.Feedback type="invalid">
                {errors.city.type === "required" && "City is required"}
                {errors.city.type === "pattern" &&
                  "Invalid characters entered for City"}
              </Form.Control.Feedback>
            )}
          </Col>
          <Form.Label column sm={3} className="text-right">
            State*
          </Form.Label>
          <Col sm={3}>
            <Form.Control
              size="sm"
              as="select"
              required
              name="state"
              value={addressDetails.state}
              onChange={handleOnChangeAddressDetails}
              onBlur={handleOnBlurInput}
              isInvalid={errors.state}
            >
              <option value="">Choose...</option>
              {states.length &&
                states.map(state => (
                  <option value={state.id}>{state.stateName}</option>
                ))}
            </Form.Control>
            {errors.state && (
              <Form.Control.Feedback type="invalid">
                {errors.state.type === "required" && "State is required"}
              </Form.Control.Feedback>
            )}
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="pincode">
          <Form.Label column sm={2} className="text-right">
            PinCode*
          </Form.Label>
          <Col sm={3}>
            <Form.Control
              size="sm"
              required
              placeholder="PinCode"
              type="text"
              name="pincode"
              value={addressDetails.pincode}
              onChange={handleOnChangeAddressDetails}
              onBlur={handleOnBlurInput}
              isInvalid={errors.pincode}
              pattern={pincodePattern}
            />

            {errors.pincode && (
              <Form.Control.Feedback type="invalid">
                {errors.pincode.type === "required" && "PinCode is required"}
                {errors.pincode.type === "pattern" &&
                  "Please enter valid PinCode"}
              </Form.Control.Feedback>
            )}
          </Col>
        </Form.Group>
      </Card.Body>
    </Card>
  );
};
export default AddressDetailsForm;
